<template>
  <BaseSimpleLayout class="master-package-list">
    <template #title>パッケージ一覧</template>
    <template v-if="loadingFlag" #button-area>
      <ScreenPermissionGate code="PACKAGE-BTN_CREATE">
        <el-button
          class="unit-master-package-list-button-create"
          type="success"
          size="large"
          @click="router.push('/master/package/create')"
        >
          パッケージ登録
        </el-button>
      </ScreenPermissionGate>
    </template>
    <template v-if="loadingFlag" #main>
      <el-form class="package-list-filter-form" @submit.prevent>
        <div class="package-list-filter-left">
          <el-form-item label="フィルター">
            <el-input
              v-model="searchText"
              clearable
              placeholder="検索"
              class="package-list-filter-input-search"
            />
          </el-form-item>
        </div>
        <div class="package-list-filter-right">
          <el-form-item label="事業区分">
            <el-select
              v-model="filterBusinessTypes"
              multiple
              collapse-tags
              collapse-tags-tooltip
              :max-collapse-tags="1"
              clearable
              placeholder="選択"
              class="package-list-filter-input-business"
            >
              <el-option label="空白" :value="PACK_FILTER_UNSET" />
              <el-option
                v-for="bt in businessTypeList"
                :key="bt.id"
                :label="bt.label"
                :value="bt.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="MCCSver">
            <el-select
              v-model="filterDeviceTypes"
              multiple
              collapse-tags
              collapse-tags-tooltip
              :max-collapse-tags="1"
              clearable
              placeholder="選択"
              class="package-list-filter-input-mccs"
            >
              <el-option label="空白" :value="PACK_FILTER_UNSET" />
              <el-option
                v-for="opt in packMccsFilterOptions"
                :key="opt.val"
                :label="opt.label"
                :value="opt.val"
              />
            </el-select>
          </el-form-item>
        </div>
      </el-form>
      <el-table :data="tableData" border :max-height="tableHeight">
        <el-table-column
          prop="id"
          label="ID"
          width="80"
          align="center"
          sortable
        />
        <el-table-column prop="name" label="名前" min-width="240" sortable>
          <template #default="scope">
            <a href="#" @click="moveDetail(scope.row.id)">{{
              scope.row.name
            }}</a>
          </template>
        </el-table-column>
        <el-table-column
          prop="packCode"
          label="品番"
          width="180"
          align="center"
          sortable
        />
        <el-table-column label="事業区分" width="140" align="center" sortable>
          <template #default="scope">
            {{ businessTypeDisplay(scope.row) }}
          </template>
        </el-table-column>
        <el-table-column label="MCCSver" width="140" align="center" sortable>
          <template #default="scope">
            {{ deviceTypeDisplay(scope.row) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="harness"
          label="専用ハーネス変換"
          width="200"
          align="center"
          sortable
        >
          <template #default="scope">
            {{ scope.row.isChangeHarnessItem ? '対象' : '対象外' }}
          </template>
        </el-table-column>
      </el-table>
    </template>
  </BaseSimpleLayout>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { useStore } from 'vuex';
  import router from '@/router';
  import BaseSimpleLayout from '@/components/BaseSimpleLayout.vue';
  import { PackagesDataState } from '@/types/master/package';
  import { ElLoading } from 'element-plus';
  import ScreenPermissionGate from '@/components/ScreenPermissionGate.vue';
  import { BusinessTypesDataState } from '@/types/master/businessType';
  import { NumberData } from '@/types/common';
  import {
    PACK_FILTER_UNSET,
    packMccsLabel,
    packMccsOptions
  } from '@/libs/pack';

  const store = useStore();
  const tableData = ref<PackagesDataState[]>([]);
  const loadingFlag = ref(false);
  const tableHeight = ref(600);
  const searchText = ref('');
  const filterBusinessTypes = ref<number[]>([]);
  const filterDeviceTypes = ref<number[]>([]);
  const businessTypeList = ref<BusinessTypesDataState[]>([]);
  const businessTypeLabels = ref<NumberData | null>(null);
  const packMccsFilterOptions = packMccsOptions.filter(
    (opt): opt is { val: number; label: string } => opt.val != null
  );

  const businessTypeDisplay = (row: PackagesDataState) => {
    if (row.businessType == null) return '-';
    return businessTypeLabels.value?.[row.businessType] ?? '-';
  };

  const deviceTypeDisplay = (row: PackagesDataState) => {
    if (row.deviceType == null) return '-';
    return packMccsLabel[row.deviceType] ?? '-';
  };

  const toFilterKey = (value: number | null | undefined): number =>
    value == null ? PACK_FILTER_UNSET : value;

  const applyFilters = () => {
    const source = store.state.packagesMaster.listData ?? [];
    // Multi-select empty → empty table (same as ship list)
    if (
      filterBusinessTypes.value.length === 0 ||
      filterDeviceTypes.value.length === 0
    ) {
      tableData.value = [];
      return;
    }

    tableData.value = source.filter((item: PackagesDataState) => {
      if (!filterBusinessTypes.value.includes(toFilterKey(item.businessType)))
        return false;
      if (!filterDeviceTypes.value.includes(toFilterKey(item.deviceType)))
        return false;

      const keyword = (searchText.value ?? '').trim();
      if (!keyword) return true;
      if (item.name.includes(keyword)) return true;
      if (item.packCode?.includes(keyword)) return true;
      const harness = item.isChangeHarnessItem ? '対象' : '対象外';
      if (harness.includes(keyword)) return true;
      return false;
    });
  };

  watch([filterBusinessTypes, filterDeviceTypes, searchText], () =>
    applyFilters()
  );

  const initialize = async () => {
    const loadingInstance = ElLoading.service({ fullscreen: true });
    await Promise.all([
      store.dispatch('packagesMaster/fetch'),
      store.dispatch('businessTypesMaster/fetch')
    ]);
    businessTypeList.value = store.state.businessTypesMaster.listData;
    businessTypeLabels.value = store.state.businessTypesMaster.replaceData;

    // Default: select all options (including 空白)
    filterBusinessTypes.value = [
      PACK_FILTER_UNSET,
      ...businessTypeList.value.map((bt) => bt.id)
    ];
    filterDeviceTypes.value = [
      PACK_FILTER_UNSET,
      ...packMccsFilterOptions.map((opt) => opt.val)
    ];

    tableHeight.value = window.innerHeight - 180;
    applyFilters();
    loadingInstance.close();
    loadingFlag.value = true;
  };

  const moveDetail = (id: number) => {
    router.push('/master/package/' + id);
  };

  initialize();
</script>
<style lang="scss">
  .master-package-list {
    .package-list-filter-form {
      width: 100%;
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      justify-content: space-between;
      gap: 8px 16px;
      margin: 16px 0 8px;

      .package-list-filter-left,
      .package-list-filter-right {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        gap: 8px 16px;
      }

      .package-list-filter-right {
        margin-left: auto;
      }

      .el-form-item {
        margin-bottom: 0;
        margin-right: 0;

        .el-form-item__label {
          font-weight: 600;
        }
      }

      .el-select {
        float: initial;

        .el-select__tags {
          flex-wrap: nowrap;
        }
      }
    }
    .package-list-filter-input-business {
      width: 200px;
    }
    .package-list-filter-input-mccs {
      width: 180px;
    }
    .package-list-filter-input-search {
      width: 240px;
    }
    .el-table {
      margin: 10px 0 0;
      thead th {
        background: #f5f7fa;
      }
    }
    .el-button {
      font-weight: 600;
    }
  }
</style>
